import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Shield, Users, Building2, FileText, FlaskConical, CheckCircle, XCircle,
  Loader2, Search, ChevronLeft, ChevronRight, Clock, AlertTriangle,
  Eye, History, Filter
} from 'lucide-react';

export default function PrashaPanel() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  // Queue states
  const [mentors, setMentors] = useState({ items: [], total: 0, page: 1 });
  const [universities, setUniversities] = useState({ items: [], total: 0, page: 1 });
  const [applications, setApplications] = useState({ items: [], total: 0, page: 1 });
  const [projects, setProjects] = useState({ items: [], total: 0, page: 1 });
  const [auditLogs, setAuditLogs] = useState({ items: [], total: 0, page: 1 });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');

  // Action dialog
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', item: null });
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      toast.error('Access denied');
      navigate('/');
      return;
    }
    fetchDashboard();
  }, [user]);

  useEffect(() => {
    if (tab === 'mentors') fetchMentors();
    else if (tab === 'universities') fetchUniversities();
    else if (tab === 'applications') fetchApplications();
    else if (tab === 'projects') fetchProjects();
    else if (tab === 'audit') fetchAuditLogs();
  }, [tab, statusFilter, searchQuery]);

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/prasha/dashboard');
      setDashboard(res.data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchMentors = async (page = 1) => {
    try {
      const res = await api.get(`/prasha/verification/mentors?status=${statusFilter}&page=${page}&search=${searchQuery}`);
      setMentors({ items: res.data.mentors, total: res.data.total, page, pages: res.data.pages });
    } catch { }
  };

  const fetchUniversities = async (page = 1) => {
    try {
      const res = await api.get(`/prasha/verification/universities?status=${statusFilter}&page=${page}&search=${searchQuery}`);
      setUniversities({ items: res.data.universities, total: res.data.total, page, pages: res.data.pages });
    } catch { }
  };

  const fetchApplications = async (page = 1) => {
    try {
      const status = statusFilter === 'pending' ? 'under_verification' : statusFilter;
      const res = await api.get(`/prasha/verification/applications?status=${status}&page=${page}&search=${searchQuery}`);
      setApplications({ items: res.data.applications, total: res.data.total, page, pages: res.data.pages });
    } catch { }
  };

  const fetchProjects = async (page = 1) => {
    try {
      const res = await api.get(`/prasha/verification/projects?status=${statusFilter}&page=${page}&search=${searchQuery}`);
      setProjects({ items: res.data.projects, total: res.data.total, page, pages: res.data.pages });
    } catch { }
  };

  const fetchAuditLogs = async (page = 1) => {
    try {
      const res = await api.get(`/prasha/audit-logs?page=${page}`);
      setAuditLogs({ items: res.data.logs, total: res.data.total, page, pages: res.data.pages });
    } catch { }
  };

  const openActionDialog = (type, item, action) => {
    setActionDialog({ open: true, type, item, action });
    setActionReason('');
  };

  const handleVerifyAction = async () => {
    if (!actionDialog.item) return;
    setActionLoading(true);

    try {
      let endpoint = '';
      const { type, item, action } = actionDialog;

      if (type === 'mentor') endpoint = `/prasha/verify/mentor/${item.user_id}`;
      else if (type === 'university') endpoint = `/prasha/verify/university/${item.user_id}`;
      else if (type === 'application') endpoint = `/prasha/verify/application/${item.application_id}`;
      else if (type === 'project') endpoint = `/prasha/verify/project/${item.project_id}`;

      await api.post(endpoint, { action, reason: actionReason });
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} ${action}d successfully`);
      setActionDialog({ open: false, type: '', item: null });

      // Refresh data
      fetchDashboard();
      if (type === 'mentor') fetchMentors(mentors.page);
      else if (type === 'university') fetchUniversities(universities.page);
      else if (type === 'application') fetchApplications(applications.page);
      else if (type === 'project') fetchProjects(projects.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const QueueStats = ({ icon: Icon, label, count, color }) => (
    <div className={`${color} rounded-xl p-6 border transition-all hover:scale-[1.02] duration-200`}>
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>{count}</p>
          <p className="text-sm font-medium opacity-90">{label}</p>
        </div>
        <div className="p-3 rounded-lg bg-white/5">
          <Icon className="h-6 w-6 opacity-80" />
        </div>
      </div>
    </div>
  );

  const Pagination = ({ data, onPageChange }) => (
    <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-700/50">
      <p className="text-sm text-slate-300 font-medium">
        Showing {data.items?.length || 0} of {data.total || 0}
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={data.page <= 1}
          onClick={() => onPageChange(data.page - 1)}
          className="border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-30"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm text-white font-semibold min-w-[100px] text-center">
          Page {data.page} of {data.pages || 1}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={data.page >= (data.pages || 1)}
          onClick={() => onPageChange(data.page + 1)}
          className="border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-30"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="prasha-panel">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>
                PRASHA CoRe Verification Panel
              </h1>
              <p className="text-xs text-slate-300 font-medium">Internal Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
              <CheckCircle className="h-3 w-3 mr-1" /> Admin
            </Badge>
            <span className="text-sm text-slate-400">{user?.email}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-800 border border-slate-700 mb-8 p-1 h-auto flex-wrap justify-start gap-1">
            <TabsTrigger value="dashboard" className="px-4 py-2 text-slate-400 data-[state=active]:bg-amber-600 data-[state=active]:text-white hover:text-slate-200 transition-colors">
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="mentors" className="px-4 py-2 text-slate-400 data-[state=active]:bg-amber-600 data-[state=active]:text-white hover:text-slate-200 transition-colors">
              Mentors {dashboard?.queues?.mentors > 0 && <Badge className="ml-2 bg-red-500 text-white text-[10px] h-4 min-w-4 flex items-center justify-center p-0">{dashboard.queues.mentors}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="universities" className="px-4 py-2 text-slate-400 data-[state=active]:bg-amber-600 data-[state=active]:text-white hover:text-slate-200 transition-colors">
              Universities {dashboard?.queues?.universities > 0 && <Badge className="ml-2 bg-red-500 text-white text-[10px] h-4 min-w-4 flex items-center justify-center p-0">{dashboard.queues.universities}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="applications" className="px-4 py-2 text-slate-400 data-[state=active]:bg-amber-600 data-[state=active]:text-white hover:text-slate-200 transition-colors">
              Applications {dashboard?.queues?.applications > 0 && <Badge className="ml-2 bg-red-500 text-white text-[10px] h-4 min-w-4 flex items-center justify-center p-0">{dashboard.queues.applications}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="projects" className="px-4 py-2 text-slate-400 data-[state=active]:bg-amber-600 data-[state=active]:text-white hover:text-slate-200 transition-colors">
              Projects {dashboard?.queues?.projects > 0 && <Badge className="ml-2 bg-red-500 text-white text-[10px] h-4 min-w-4 flex items-center justify-center p-0">{dashboard.queues.projects}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="audit" className="px-4 py-2 text-slate-400 data-[state=active]:bg-amber-600 data-[state=active]:text-white hover:text-slate-200 transition-colors">
              <History className="h-4 w-4 mr-2" /> Audit Log
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-amber-500 animate-spin" /></div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  <QueueStats icon={Users} label="Pending Mentors" count={dashboard?.queues?.mentors || 0} color="bg-blue-500/10 text-blue-400 border-blue-500/20" />
                  <QueueStats icon={Building2} label="Pending Universities" count={dashboard?.queues?.universities || 0} color="bg-purple-500/10 text-purple-400 border-purple-500/20" />
                  <QueueStats icon={FileText} label="Pending Applications" count={dashboard?.queues?.applications || 0} color="bg-amber-500/10 text-amber-400 border-amber-500/20" />
                  <QueueStats icon={FlaskConical} label="Pending Projects" count={dashboard?.queues?.projects || 0} color="bg-emerald-500/10 text-emerald-400 border-emerald-500/20" />
                </div>

                <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Manrope' }}>Recent Actions</h3>
                  {dashboard?.recent_actions?.length === 0 ? (
                    <p className="text-slate-400 text-sm">No recent actions</p>
                  ) : (
                    <div className="flex flex-col gap-4">
                      {dashboard?.recent_actions?.slice(0, 10).map((log) => (
                        <div key={log.log_id} className="flex items-start gap-4 py-4 border-b border-slate-700/50 last:border-0 hover:bg-slate-700/20 transition-all px-3 rounded-xl bg-slate-800/20">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${log.action.includes('approve') ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                            {log.action.includes('approve') ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white leading-relaxed">
                              <span className="font-semibold text-amber-400">{log.admin_email}</span>
                              <span className="mx-1 text-slate-300">{log.action.replace('_', ' ')}</span>
                            </p>
                            <p className="text-xs text-slate-300 mt-1 font-medium">
                              {log.target_type}: <span className="text-white">{log.details?.mentor_name || log.details?.university_name || log.details?.student_name || log.details?.title || log.target_id}</span>
                            </p>
                            {log.reason && (
                              <div className="mt-2 p-2 bg-slate-900/50 rounded border border-slate-700/50">
                                <p className="text-xs text-slate-300 italic">Reason: {log.reason}</p>
                              </div>
                            )}
                          </div>
                          <div className="text-right flex flex-col items-end gap-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Timestamp</span>
                            <span className="text-xs text-slate-300 whitespace-nowrap">{new Date(log.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </TabsContent>

          {/* Mentors Tab */}
          <TabsContent value="mentors">
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Manrope' }}>Mentor Verification Queue</h3>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 bg-slate-700 border-slate-600 text-white w-48"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="bg-slate-700 border border-slate-600 rounded-md px-3 text-sm text-white"
                  >
                    <option value="pending">Pending</option>
                    <option value="verified">Verified</option>
                  </select>
                </div>
              </div>

              {mentors.items?.length === 0 ? (
                <p className="text-slate-400 text-sm py-8 text-center">No mentors in queue</p>
              ) : (
                <div className="flex flex-col gap-5">
                  {mentors.items?.map((mentor) => (
                    <div key={mentor.user_id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white">{mentor.name}</p>
                          <Badge className={mentor.verified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                            {mentor.verified ? 'Verified' : 'Pending'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-400">{mentor.email}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {mentor.profile?.institution || 'No institution'} • {mentor.profile?.specialization || 'No specialization'}
                        </p>
                      </div>
                      {!mentor.verified && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => openActionDialog('mentor', mentor, 'approve')} className="bg-emerald-600 hover:bg-emerald-700">
                            <CheckCircle className="h-4 w-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openActionDialog('mentor', mentor, 'reject')} className="border-red-500 text-red-400 hover:bg-red-500/20">
                            <XCircle className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <Pagination data={mentors} onPageChange={fetchMentors} />
            </div>
          </TabsContent>

          {/* Universities Tab */}
          <TabsContent value="universities">
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Manrope' }}>University Verification Queue</h3>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 bg-slate-700 border-slate-600 text-white w-48" />
                  </div>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="bg-slate-700 border border-slate-600 rounded-md px-3 text-sm text-white">
                    <option value="pending">Pending</option>
                    <option value="verified">Verified</option>
                  </select>
                </div>
              </div>

              {universities.items?.length === 0 ? (
                <p className="text-slate-400 text-sm py-8 text-center">No universities in queue</p>
              ) : (
                <div className="flex flex-col gap-5">
                  {universities.items?.map((uni) => (
                    <div key={uni.user_id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white">{uni.profile?.university_name || uni.name}</p>
                          <Badge className={uni.verified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                            {uni.verified ? 'Verified' : 'Pending'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-400">{uni.email}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {uni.profile?.accreditation || 'No accreditation info'} • {uni.profile?.country || ''}
                        </p>
                      </div>
                      {!uni.verified && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => openActionDialog('university', uni, 'approve')} className="bg-emerald-600 hover:bg-emerald-700">
                            <CheckCircle className="h-4 w-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openActionDialog('university', uni, 'reject')} className="border-red-500 text-red-400 hover:bg-red-500/20">
                            <XCircle className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <Pagination data={universities} onPageChange={fetchUniversities} />
            </div>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Manrope' }}>Research Application Verification</h3>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 bg-slate-700 border-slate-600 text-white w-48" />
                  </div>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="bg-slate-700 border border-slate-600 rounded-md px-3 text-sm text-white">
                    <option value="pending">Under Verification</option>
                    <option value="verified">Verified</option>
                    <option value="rejected_by_verification">Rejected</option>
                    <option value="all">All</option>
                  </select>
                </div>
              </div>

              {applications.items?.length === 0 ? (
                <p className="text-slate-400 text-sm py-8 text-center">No applications in queue</p>
              ) : (
                <div className="flex flex-col gap-6">
                  {applications.items?.map((app) => (
                    <div key={app.application_id} className="p-4 bg-slate-700/50 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-white">{app.student_details?.name || app.student_name}</p>
                            <Badge className={
                              app.status === 'verified' ? 'bg-emerald-500/20 text-emerald-400' :
                                app.status === 'rejected_by_verification' ? 'bg-red-500/20 text-red-400' :
                                  'bg-amber-500/20 text-amber-400'
                            }>
                              {app.status?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-400">Applying to: {app.project_details?.title || app.project_title}</p>
                          <p className="text-xs text-slate-500">
                            {app.student_details?.university || ''} • {app.project_details?.specialty || ''} • {app.project_details?.mentor_name || ''}
                          </p>

                          {/* Motivation Letter Preview */}
                          <div className="mt-3 p-3 bg-slate-800 rounded-lg">
                            <p className="text-xs text-slate-400 mb-1">Motivation Letter:</p>
                            <p className="text-sm text-slate-300 line-clamp-3">{app.motivation_letter || app.cover_letter || 'No letter provided'}</p>
                          </div>
                        </div>

                        {app.status === 'under_verification' && (
                          <div className="flex gap-2 ml-4">
                            <Button size="sm" onClick={() => openActionDialog('application', app, 'approve')} className="bg-emerald-600 hover:bg-emerald-700">
                              <CheckCircle className="h-4 w-4 mr-1" /> Verify
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openActionDialog('application', app, 'reject')} className="border-red-500 text-red-400 hover:bg-red-500/20">
                              <XCircle className="h-4 w-4 mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Pagination data={applications} onPageChange={fetchApplications} />
            </div>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Manrope' }}>Project Approval Queue</h3>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 bg-slate-700 border-slate-600 text-white w-48" />
                  </div>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="bg-slate-700 border border-slate-600 rounded-md px-3 text-sm text-white">
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              {projects.items?.length === 0 ? (
                <p className="text-slate-400 text-sm py-8 text-center">No projects in queue</p>
              ) : (
                <div className="flex flex-col gap-5">
                  {projects.items?.map((proj) => (
                    <div key={proj.project_id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white">{proj.title}</p>
                          <Badge className={
                            proj.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                              proj.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                'bg-amber-500/20 text-amber-400'
                          }>
                            {proj.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-400">by {proj.mentor_details?.name || proj.mentor_name}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {proj.specialty} • {proj.duration} • {proj.mode} • {proj.paid ? `$${proj.price}` : 'Free'}
                        </p>
                      </div>
                      {proj.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => openActionDialog('project', proj, 'approve')} className="bg-emerald-600 hover:bg-emerald-700">
                            <CheckCircle className="h-4 w-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openActionDialog('project', proj, 'reject')} className="border-red-500 text-red-400 hover:bg-red-500/20">
                            <XCircle className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <Pagination data={projects} onPageChange={fetchProjects} />
            </div>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6" style={{ fontFamily: 'Manrope' }}>Audit Log</h3>
              {auditLogs.items?.length === 0 ? (
                <p className="text-slate-400 text-sm py-8 text-center">No audit logs</p>
              ) : (
                <div className="flex flex-col gap-5">
                  {auditLogs.items?.map((log) => (
                    <div key={log.log_id} className="flex items-start gap-4 p-4 bg-slate-700/30 rounded-xl border border-slate-700/50 hover:bg-slate-700/50 transition-colors">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${log.action.includes('approve') ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                        {log.action.includes('approve') ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm text-white">
                            <span className="font-semibold text-amber-400">{log.admin_email}</span>
                            <span className="mx-1 text-slate-300"> performed </span>
                            <span className="font-bold text-emerald-400 uppercase text-[10px] tracking-widest">{log.action.replace(/_/g, ' ')}</span>
                          </p>
                          <span className="text-[10px] text-slate-400 font-mono">{new Date(log.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-xs text-slate-300 font-medium">
                          Target Entity: <span className="text-white bg-slate-900/50 px-1.5 py-0.5 rounded ml-1">{log.target_type} ({log.target_id})</span>
                        </p>
                        {log.reason && (
                          <div className="mt-2 p-2 bg-slate-900/50 rounded border border-slate-700/30">
                            <p className="text-xs text-slate-300 italic line-clamp-2">Reason: {log.reason}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Pagination data={auditLogs} onPageChange={fetchAuditLogs} />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ ...actionDialog, open })}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {actionDialog.action === 'approve' ? 'Approve' : 'Reject'} {actionDialog.type}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <p className="text-sm">
                {actionDialog.type === 'mentor' && actionDialog.item?.name}
                {actionDialog.type === 'university' && (actionDialog.item?.profile?.university_name || actionDialog.item?.name)}
                {actionDialog.type === 'application' && `${actionDialog.item?.student_name} → ${actionDialog.item?.project_title}`}
                {actionDialog.type === 'project' && actionDialog.item?.title}
              </p>
            </div>
            <div>
              <Label className="text-slate-300">Reason (optional)</Label>
              <Textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder={`Reason for ${actionDialog.action}ing...`}
                className="bg-slate-700 border-slate-600 text-white mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ ...actionDialog, open: false })} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button
              onClick={handleVerifyAction}
              disabled={actionLoading}
              className={actionDialog.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {actionDialog.action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
